/**
 * HolidayDecisionTable
 * Table for making holiday work decisions
 *
 * @version 2025.1.0
 */
import React from 'react';
import {
  Switch,
  Typography,
  Chip,
  Box,
} from '@mui/material';
import { DataTable, type DataTableColumn } from '../../../components/common';
import { HolidayDecision, HolidayScope } from '../../../services/scheduleReleaseService';

interface HolidayDecisionTableProps {
  decisions: HolidayDecision[];
  onDecisionChange: (holidayId: number, workOnHoliday: boolean) => void;
}

const SCOPE_LABELS: Record<HolidayScope, string> = {
  NATIONAL: 'Nacional',
  STATE: 'Estadual',
  MUNICIPAL: 'Municipal',
  COMPANY: 'Empresa',
};

const SCOPE_COLORS: Record<HolidayScope, 'error' | 'warning' | 'info' | 'default'> = {
  NATIONAL: 'error',
  STATE: 'warning',
  MUNICIPAL: 'info',
  COMPANY: 'default',
};

const HolidayDecisionTable: React.FC<HolidayDecisionTableProps> = ({
  decisions,
  onDecisionChange,
}) => {
  if (decisions.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
        Nenhum feriado no período selecionado.
      </Typography>
    );
  }

  const columns: DataTableColumn<HolidayDecision>[] = [
    {
      id: 'date',
      label: 'Data',
      render: (decision) => {
        const date = new Date(decision.date);
        return (
          <Box>
            <Typography variant="body2">{date.toLocaleDateString('pt-BR')}</Typography>
            <Typography variant="caption" color="text.secondary">
              {date.toLocaleDateString('pt-BR', { weekday: 'short' })}
            </Typography>
          </Box>
        );
      },
    },
    {
      id: 'name',
      label: 'Feriado',
      primary: true,
      render: (decision) => <Typography variant="body2">{decision.name}</Typography>,
    },
    {
      id: 'scope',
      label: 'Tipo',
      render: (decision) => (
        <Chip
          label={SCOPE_LABELS[decision.scope]}
          color={SCOPE_COLORS[decision.scope]}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      id: 'workOnHoliday',
      label: 'Trabalhar',
      align: 'center',
      render: (decision) => (
        <Switch
          checked={decision.workOnHoliday}
          onChange={(e) => onDecisionChange(decision.holidayId, e.target.checked)}
          color="success"
        />
      ),
    },
  ];

  return (
    <DataTable
      columns={columns}
      rows={decisions}
      getRowId={(decision) => decision.holidayId}
      emptyState={{
        title: 'Nenhum feriado no período selecionado',
        description: 'Os feriados vêm do período escolhido logo acima nesta liberação de agenda.',
      }}
    />
  );
};

export default HolidayDecisionTable;
